import { useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Copy, Check } from "lucide-react";
import { Dialog } from "@/components/ui/Dialog";
import { Alert } from "@/components/ui/Alert";
import { ApiFieldErrors } from "@/components/ui/ApiFieldErrors";
import { apiFieldDescribedBy, apiFieldHasError } from "@/components/ui/api-field-errors";
import { isStaleSessionError } from "@/lib/api/client";
import { useCreateAccessKey } from "../hooks/useAccessKeys";
import { StaleSessionNotice } from "./StaleSessionNotice";

const DAY_MS = 24 * 60 * 60 * 1000;

interface CreateAccessKeyDialogProps {
  open: boolean;
  onClose: () => void;
}

export function CreateAccessKeyDialog({ open, onClose }: CreateAccessKeyDialogProps) {
  const { t } = useTranslation();
  const createKey = useCreateAccessKey();
  const [friendlyName, setFriendlyName] = useState("");
  const [ttlDays, setTtlDays] = useState("");
  const [copied, setCopied] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);
  const copyTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const copyGen = useRef(0);
  const fieldErrorPrefix = useId();
  const ttlHintId = useId();
  const secretWarningId = useId();

  const secret = createKey.data?.accessKey.name;
  const staleSession = isStaleSessionError(createKey.error);
  const serverError = staleSession ? null : createKey.error;
  const nameInvalid = apiFieldHasError(serverError, "friendlyName");
  const ttlInvalid = apiFieldHasError(serverError, "ttl");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (createKey.isPending) return;
    if (!friendlyName.trim()) return;

    const days = Number(ttlDays);
    createKey.mutate({
      friendlyName: friendlyName.trim(),
      ...(ttlDays.trim() !== "" && days > 0 ? { ttl: days * DAY_MS } : {}),
    });
  };

  const clearFieldError = () => {
    if (serverError) createKey.reset();
  };

  const handleClose = () => {
    if (!createKey.isPending) {
      setFriendlyName("");
      setTtlDays("");
      setCopied(false);
      setCopyFailed(false);
      copyGen.current += 1;
      if (copyTimer.current) clearTimeout(copyTimer.current);
      createKey.reset();
      onClose();
    }
  };

  const copySecret = async () => {
    if (!secret) return;
    if (copyTimer.current) clearTimeout(copyTimer.current);
    const gen = copyGen.current;
    try {
      await navigator.clipboard.writeText(secret);
      if (gen !== copyGen.current) return;
      setCopyFailed(false);
      setCopied(true);
      copyTimer.current = setTimeout(() => setCopied(false), 2000);
    } catch {
      if (gen !== copyGen.current) return;
      setCopied(false);
      setCopyFailed(true);
    }
  };

  const inputClass =
    "h-11 w-full rounded-lg border border-border bg-surface px-3.5 text-sm text-heading transition-colors placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-indigo/40 focus:border-indigo aria-disabled:opacity-50 hover:border-border-light";

  return (
    <Dialog open={open} onClose={handleClose} title={t("account.createAccessKey")}>
      {secret ? (
        <div className="space-y-4">
          <Alert id={secretWarningId} variant="warning">
            {t("account.accessKeySecretWarning")}
          </Alert>
          <div className="flex items-center gap-2 rounded-lg bg-elevated p-3">
            <code className="flex-1 break-all font-mono text-xs text-heading">{secret}</code>
            <button
              type="button"
              onClick={copySecret}
              autoFocus
              aria-label={copied ? t("common.copied") : t("common.copy")}
              aria-describedby={secretWarningId}
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted hover:bg-surface hover:text-heading"
            >
              {copied ? <Check size={14} className="text-success" /> : <Copy size={14} />}
            </button>
          </div>
          {copyFailed && (
            <p role="alert" className="text-xs text-danger">
              {t("common.copyFailed")}
            </p>
          )}
          <button
            type="button"
            onClick={handleClose}
            className="w-full rounded-lg bg-indigo px-4 py-2.5 text-sm font-semibold text-white transition-all hover:bg-indigo-bright"
          >
            {t("common.confirm")}
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="access-key-name" className="text-sm font-medium text-subtle">
              {t("account.accessKeyNameLabel")}
            </label>
            <input
              id="access-key-name"
              type="text"
              value={friendlyName}
              onChange={(e) => {
                setFriendlyName(e.target.value);
                clearFieldError();
              }}
              placeholder={t("account.accessKeyNamePlaceholder")}
              data-autofocus={staleSession ? undefined : true}
              readOnly={createKey.isPending}
              aria-disabled={createKey.isPending || undefined}
              aria-invalid={nameInvalid ? true : undefined}
              aria-describedby={apiFieldDescribedBy(serverError, "friendlyName", fieldErrorPrefix)}
              className={inputClass}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="access-key-ttl" className="text-sm font-medium text-subtle">
              {t("account.accessKeyTtlLabel")}
            </label>
            <input
              id="access-key-ttl"
              type="number"
              min={1}
              value={ttlDays}
              onChange={(e) => {
                setTtlDays(e.target.value);
                clearFieldError();
              }}
              placeholder="60"
              readOnly={createKey.isPending}
              aria-disabled={createKey.isPending || undefined}
              aria-invalid={ttlInvalid ? true : undefined}
              aria-describedby={apiFieldDescribedBy(
                serverError,
                "ttl",
                fieldErrorPrefix,
                ttlHintId,
              )}
              className={inputClass}
            />
            <p id={ttlHintId} className="text-xs text-muted">
              {t("account.accessKeyTtlHint")}
            </p>
          </div>

          {!staleSession && (
            <ApiFieldErrors
              error={createKey.error}
              fieldLabels={{
                friendlyName: t("account.accessKeyNameLabel"),
                ttl: t("account.accessKeyTtlLabel"),
              }}
              idPrefix={fieldErrorPrefix}
            />
          )}
          {staleSession && (
            <StaleSessionNotice
              returnTo="/account"
              body={t("account.staleSession.accessKeyBody")}
              autoFocus
            />
          )}

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={handleClose}
              aria-disabled={createKey.isPending || undefined}
              className="rounded-lg border border-border bg-surface px-4 py-2.5 text-sm font-medium text-muted transition-colors hover:border-border-light hover:text-heading aria-disabled:opacity-50 aria-disabled:cursor-not-allowed"
            >
              {t("common.cancel")}
            </button>
            <button
              type="submit"
              disabled={!createKey.isPending && (!friendlyName.trim() || staleSession)}
              aria-disabled={createKey.isPending || undefined}
              aria-busy={createKey.isPending || undefined}
              className="rounded-lg bg-indigo px-4 py-2.5 text-sm font-semibold text-white transition-all hover:bg-indigo-bright disabled:opacity-50 disabled:cursor-not-allowed aria-disabled:opacity-50 aria-disabled:cursor-not-allowed"
            >
              {createKey.isPending ? t("account.creatingAccessKey") : t("account.createAccessKey")}
            </button>
          </div>
        </form>
      )}
    </Dialog>
  );
}
